/**
 * ProjectionPlaneSnapping - Manages projection plane selection and snapping.
 *
 * Handles hover visualization, click-to-add/remove, and point projection
 * onto 1-3 planes. Owns the helper line that visualizes projection steps.
 * Any measure tool can use this by creating an instance in onEnable.
 */

import * as THREE from 'three';
import { Viewer } from '../../../../viewer/viewer';
import { PointoramaPlane } from '../../../../utils/PointoramaPlane';
import { HelperLine } from '../../../../utils/HelperLine';
import { FloorplanLine } from '../../../../utils/FloorplanLine';
import { DefaultColors } from '../../../../defines';
import { Utils } from '../../../../utils';
import { projectOnLine } from './GeometryUtils';

/**
 * A plane-like object that measure-tool projection snapping can project onto.
 * Both PointoramaPlane and PointoramaSurface satisfy this (surfaces replaced the
 * former plane annotations). It must be a THREE.Object3D so hover/selection
 * markers can be attached as children.
 */
export type ProjectionTarget = THREE.Object3D & {
  position: THREE.Vector3;
  radius: number;
  getNormal(): THREE.Vector3;
  getPointProjection(point: THREE.Vector3): THREE.Vector3;
  getAngleBetweenPlanes(other: { getNormal(): THREE.Vector3 }): number;
  containsPoint(point: THREE.Vector3): boolean;
};

export class ProjectionPlaneSnapping {
  private planes: ProjectionTarget[] = [];
  private planeMarkers!: PointoramaPlane[];
  private hoveredPlane: ProjectionTarget | null = null;
  private hoveredPlaneMarker!: PointoramaPlane;
  private helperLine!: HelperLine;
  private _lastSnapWasPerpendicular = false;

  constructor(private viewer: Viewer) {}

  /** Initialize markers and helper line, add to scene. */
  init(scene: THREE.Scene): void {
    this.hoveredPlaneMarker = new PointoramaPlane();
    this.hoveredPlaneMarker.setThickness(0.03);
    this.hoveredPlaneMarker.fill(true, { color: DefaultColors.HIGHLIGHT, opacity: 0.5 });

    this.planeMarkers = [new PointoramaPlane(), new PointoramaPlane(), new PointoramaPlane()];
    for (const p of this.planeMarkers) {
      p.fill(true, { color: DefaultColors.HIGHLIGHT, opacity: 0.8 });
    }

    this.helperLine = new HelperLine();
    this.helperLine.visible = false;
    scene.add(this.helperLine);
  }

  /** Clean up markers and helper line, remove from scene. */
  dispose(scene: THREE.Scene): void {
    scene.remove(this.helperLine);
    this.hoveredPlane?.remove(this.hoveredPlaneMarker);
    for (let i = 0; i < 3; i++) {
      this.planes.at(i)?.remove(this.planeMarkers[i]);
    }
    this.planes = [];
    this.hoveredPlane = null;
  }

  /** Update hover state for planes. Call per-frame. */
  updateHover(): void {
    if (this.viewer.viewerMode.in2DMode) {
      this.hoveredPlane = null;
      Utils.changeCursor('default');
      return;
    }
    this.hoveredPlane?.remove(this.hoveredPlaneMarker);

    // Surfaces replaced plane annotations as the snap target.
    this.hoveredPlane = (this.viewer.inputHandler.getHoveredSurface() as ProjectionTarget | null) || null;

    if (this.hoveredPlane) {
      if (this.planes.includes(this.hoveredPlane)) {
        this.hoveredPlaneMarker.fill(false);
      } else if (this.canAdd(this.hoveredPlane)) {
        this.hoveredPlaneMarker.fill(true, { color: DefaultColors.HIGHLIGHT, opacity: 0.5 });
      } else {
        this.hoveredPlaneMarker.fill(true, { color: new THREE.Color(0xff0000), opacity: 0.5 });
      }
      this.hoveredPlaneMarker.setRadius(this.hoveredPlane.radius);
      this.hoveredPlane.add(this.hoveredPlaneMarker);
      Utils.changeCursor('pointer');
    } else {
      Utils.changeCursor('default');
    }
  }

  /** Get the currently hovered projection target (null if none). */
  getHoveredPlane(): ProjectionTarget | null {
    return this.hoveredPlane;
  }

  /** Handle click on a plane. Left-click adds, right-click or re-click removes. */
  onClick(button: THREE.MOUSE): void {
    if (!this.hoveredPlane) return;

    // Remove markers from current planes
    this.planes.at(0)?.remove(this.planeMarkers[0]);
    this.planes.at(1)?.remove(this.planeMarkers[1]);
    this.planes.at(2)?.remove(this.planeMarkers[2]);

    if (button === THREE.MOUSE.RIGHT || this.planes.includes(this.hoveredPlane)) {
      this.planes = this.planes.filter((p) => p !== this.hoveredPlane);
    } else if (button === THREE.MOUSE.LEFT) {
      if (this.canAdd(this.hoveredPlane)) {
        this.planes.push(this.hoveredPlane);
      }
    }

    // Re-add markers to updated planes
    this.planes.at(0)?.add(this.planeMarkers[0]);
    this.planes.at(1)?.add(this.planeMarkers[1]);
    this.planes.at(2)?.add(this.planeMarkers[2]);

    this.planeMarkers[0].setRadius(this.planes[0]?.radius || 0.1);
    this.planeMarkers[1].setRadius(this.planes[1]?.radius || 0.1);
    this.planeMarkers[2].setRadius(this.planes[2]?.radius || 0.1);
  }

  /** Whether any projection planes are currently active. */
  hasPlanes(): boolean {
    return this.planes.length > 0;
  }

  /** Get the single active projection plane, or null if 0 or 2+ planes are active. */
  getSinglePlane(): ProjectionTarget | null {
    return this.planes.length === 1 ? this.planes[0] : null;
  }

  /** Whether the last snap() chose perpendicular-from-plane instead of on-plane projection. */
  get lastSnapWasPerpendicular(): boolean {
    return this._lastSnapWasPerpendicular;
  }

  /** Project a point onto the active projection planes. Updates helper line. */
  snap(point: THREE.Vector3, previousPoint?: THREE.Vector3): THREE.Vector3 {
    this._lastSnapWasPerpendicular = false;

    // Temporarily include hovered plane for preview
    let projectingOnHoveredPlane = false;
    if (this.hoveredPlane && this.canAdd(this.hoveredPlane)) {
      this.planes.push(this.hoveredPlane);
      projectingOnHoveredPlane = true;
    }

    const projectionPoint = this.planes.length === 3 ? this.projectOnThreePlanes(point) : this.planes.length === 2 ? this.projectOnTwoPlanes(point) : this.planes.length === 1 ? this.projectOnOnePlane(point, previousPoint) : point;

    if (projectingOnHoveredPlane) this.planes.pop();
    return projectionPoint;
  }

  /** Update helper line visibility based on active planes. */
  updateHelperLine(): void {
    this.helperLine.visible = this.planes.length > 0;
  }

  private canAdd(plane: ProjectionTarget): boolean {
    if (this.planes.length >= 3) return false;

    for (const projectionPlane of this.planes) {
      if (projectionPlane === plane) return false;
      const angle = projectionPlane.getAngleBetweenPlanes(plane);
      if (angle < 0.01) return false;
    }

    return true;
  }

  private projectOnThreePlanes(point: THREE.Vector3): THREE.Vector3 {
    const sectionLine1 = new FloorplanLine();
    const sectionLine2 = new FloorplanLine();
    sectionLine1.defineFromIntersectingPlanes(this.planes[0], this.planes[1]);
    sectionLine2.defineFromIntersectingPlanes(this.planes[1], this.planes[2]);
    const projectionPoint1 = this.planes[0].getPointProjection(point);
    const projectionPoint2 = projectOnLine(point, sectionLine1.getStart(), sectionLine1.direction);
    const projectionPoint3 = sectionLine1.findPointOfIntersectionWithOtherLine(sectionLine2);
    if (projectionPoint3) {
      this.helperLine.setBetweenPoints([point, projectionPoint1, projectionPoint2, projectionPoint3]);
      return projectionPoint3;
    }
    return point;
  }

  private projectOnTwoPlanes(point: THREE.Vector3): THREE.Vector3 {
    const sectionLine = new FloorplanLine();
    sectionLine.defineFromIntersectingPlanes(this.planes[0], this.planes[1]);
    const projectionPoint1 = this.planes[0].getPointProjection(point);
    const projectionPoint2 = projectOnLine(point, sectionLine.getStart(), sectionLine.direction);
    this.helperLine.setBetweenPoints([point, projectionPoint1, projectionPoint2]);
    return projectionPoint2;
  }

  private projectOnOnePlane(point: THREE.Vector3, previousPoint?: THREE.Vector3): THREE.Vector3 {
    const projectionPoint = this.planes[0].getPointProjection(point);

    // When a previous point exists, also consider projecting along the plane normal
    // from that point. Pick whichever (on-plane vs perpendicular) is closer to the mouse.
    if (previousPoint) {
      const perpendicularPoint = projectOnLine(point, previousPoint, this.planes[0].getNormal());
      if (perpendicularPoint.distanceTo(point) < projectionPoint.distanceTo(point)) {
        this._lastSnapWasPerpendicular = true;
        this.helperLine.setBetweenPoints([point, perpendicularPoint]);
        return perpendicularPoint;
      }
    }

    this.helperLine.setBetweenPoints([point, projectionPoint]);
    return projectionPoint;
  }
}
